import type { FC } from 'react';
import cn from 'classnames';
import { RiCheckLine } from 'react-icons/ri';
import styles from './CircularProgress.module.scss';

export type CircularProgressStatus = 'active' | 'success' | 'error';

interface CircularProgressProps {
  percent: number;
  status?: CircularProgressStatus;
  size?: number;
  strokeWidth?: number;
  className?: string;
  testId?: string;
}

const DEFAULT_SIZE = 132;
const DEFAULT_STROKE_WIDTH = 6;
const VIEW_BOX = 100;
const CENTER = VIEW_BOX / 2;

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

/**
 * Circular progress indicator. Renders an SVG ring filled clockwise from
 * the top to the given `percent` (0-100). When `percent` reaches 100 and
 * the caller has not forced `status="error"`, the indicator is promoted to
 * `success` and a check mark replaces the percentage label — matching the
 * behaviour previously provided by `react-sweet-progress`.
 */
const CircularProgress: FC<CircularProgressProps> = ({
  percent,
  status,
  size = DEFAULT_SIZE,
  strokeWidth = DEFAULT_STROKE_WIDTH,
  className,
  testId = 'circular-progress',
}) => {
  const safePercent = clamp(Number.isFinite(percent) ? percent : 0, 0, 100);
  const effectiveStatus: CircularProgressStatus =
    status === 'error' ? 'error' : safePercent >= 100 ? 'success' : (status ?? 'active');

  const radius = CENTER - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - safePercent / 100);

  const pathClassName = cn(styles.path, {
    [styles.pathActive]: effectiveStatus === 'active',
    [styles.pathSuccess]: effectiveStatus === 'success',
    [styles.pathError]: effectiveStatus === 'error',
  });

  const symbolClassName = cn(styles.symbol, {
    [styles.symbolSuccess]: effectiveStatus === 'success',
    [styles.symbolError]: effectiveStatus === 'error',
  });

  const symbolFontSize = Math.round(size * 0.2);
  const displayPercent = Math.floor(safePercent);

  return (
    <div
      className={cn(styles.circularProgress, className)}
      style={{ width: size, height: size }}
      data-test-component="CircularProgress"
      data-test-element="container"
      data-testid={testId}
      role="progressbar"
      aria-valuenow={displayPercent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Progress: ${displayPercent}%`}
    >
      <svg
        viewBox={`0 0 ${VIEW_BOX} ${VIEW_BOX}`}
        width={size}
        height={size}
        data-test-element="svg"
        aria-hidden="true"
        focusable="false"
      >
        <circle
          className={styles.trail}
          cx={CENTER}
          cy={CENTER}
          r={radius}
          strokeWidth={strokeWidth}
          data-test-element="trail"
        />
        {safePercent > 0 && (
          <circle
            className={pathClassName}
            cx={CENTER}
            cy={CENTER}
            r={radius}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            data-test-element="path"
          />
        )}
      </svg>
      <span
        className={symbolClassName}
        style={{ fontSize: symbolFontSize }}
        data-test-element="symbol"
        data-testid={`${testId}-symbol`}
      >
        {effectiveStatus === 'success' ? (
          <RiCheckLine size={symbolFontSize * 1.2} aria-hidden="true" />
        ) : (
          `${displayPercent}%`
        )}
      </span>
    </div>
  );
};

export default CircularProgress;
